import { IWasmCustomData, TRoutePopupInfoColorVariant, TWasmId } from "~/interfaces";
import Wasm from "./wasm";
import { TWasmTextureNames } from "./textures-data";
import { getPathCaption, getReview, getText, getRoutePointCircle, createRoutePopupContentLines, getWeatherIconTextureCanvas, getWasmTexture, createRoutePopupPubtSummary, createRoutePopupPubtSection, createRoutePopupClosure } from "./textures-graphics";
import { AUTO_TEXTURES, CUSTOM_TEXTURES, DEF_POPUP_ARROW_ANGLES, getTextureName, MAIN_TEXTURES, TCustomTexturesNames, TEXTURES_CONF, WASM_TEXTURES } from "./const";
import { TUrlProviderNamesVariants } from "./interfaces";
import { TPositionShareImageVariant } from "./store/interfaces";
import { CONFIG } from "~/config";

interface ICollisionGroupItem {
	group: Module.CollLogicGroup;
	name: string;
}

interface IPopupMappingItem {
	frame: Module.FrameDescriptor;
	arrow: Module.ArrowMapping;
}

interface ICollisionGroups {
	searchDrop: ICollisionGroupItem;
	searchText: ICollisionGroupItem;
	routePopupInfo: ICollisionGroupItem;
	routePopupWeather: ICollisionGroupItem;
	routePopupPubt: ICollisionGroupItem;
	routePopupClosure: ICollisionGroupItem;
}

interface IPopupMapping {
	infoWhite: IPopupMappingItem;
	infoBlue: IPopupMappingItem;
	weather: IPopupMappingItem;
	closure: IPopupMappingItem;
}

function getUrlProvider(name: TUrlProviderNamesVariants) {
	return getTextureName(name, true);
}

function createCollisionGroups(module: typeof Module): ICollisionGroups {
	const groupDrop = {
		name: "drop",
		value: 0,
	};
	const groupTexts = {
		name: "texts",
		value: 1,
	};
	const groupPopupInfo = {
		name: "popup-info",
		value: 2,
	};
	const groupPopupWeather = {
		name: "popup-weather",
		value: 3,
	};
	const groupPopupPubt = {
		name: "popup-pubt",
		value: 4,
	};
	const groupPopupClosure = {
		name: "popup-closure",
		value: 5,
	};

	const groupSearchDrop = new module.CollLogicGroup(groupDrop.name, groupDrop.value, module.CollGroupMode.CollisionSrcOnly);
	const groupSearchText = new module.CollLogicGroup(groupTexts.name, groupTexts.value, module.CollGroupMode.SinglePartVisible);
	const groupRoutePopupInfo = new module.CollLogicGroup(groupPopupInfo.name, groupPopupInfo.value, module.CollGroupMode.SinglePartVisible);
	const groupRoutePopupWeather = new module.CollLogicGroup(groupPopupWeather.name, groupPopupWeather.value, module.CollGroupMode.SinglePartVisible);
	const groupRoutePopupPubt = new module.CollLogicGroup(groupPopupPubt.name, groupPopupPubt.value, module.CollGroupMode.SinglePartVisible);
	const groupRoutePopupClosure = new module.CollLogicGroup(groupPopupClosure.name, groupPopupClosure.value, module.CollGroupMode.SinglePartVisible);

	return {
		searchDrop: {
			group: groupSearchDrop,
			name: groupDrop.name,
		},
		searchText: {
			group: groupSearchText,
			name: groupTexts.name,
		},
		routePopupInfo: {
			group: groupRoutePopupInfo,
			name: groupPopupInfo.name,
		},
		routePopupWeather: {
			group: groupRoutePopupWeather,
			name: groupPopupWeather.name,
		},
		routePopupPubt: {
			group: groupRoutePopupPubt,
			name: groupPopupPubt.name,
		},
		routePopupClosure: {
			group: groupRoutePopupClosure,
			name: groupPopupClosure.name,
		},
	};
}

function createPopupMappings(module: typeof Module, scale: number): IPopupMapping {
	const version = CONFIG.version;
	const scalePath = `${scale}x`;
	// todor velikosti u verzi 1x, 3x
	const getInfoFrame = (isActive: boolean) => new module.FrameDescriptor(
		0, 50,
		0, 52,
		26, 18,
		15, 15,
		1.0,
		isActive
			? `/img/wasm-popups/${scalePath}/wasm-popup-info-blue.png?version=${version}`
			: `/img/wasm-popups/${scalePath}/wasm-popup-info-white.png?version=${version}`,
	);
	const infoWhiteFrame = getInfoFrame(false);
	const infoBlueFrame = getInfoFrame(true);
	const weatherFrame = new module.FrameDescriptor(
		0, 39,
		0, 34,
		21, 14,
		// 20 tluste, 25 lepsi
		20, 20,
		1.0,
		`/img/wasm-popups/${scalePath}/wasm-popup-weather.png?version=${version}`,
	);
	const closureFrame = new module.FrameDescriptor(
		0, 70,
		0, 70,
		39, 28,
		// 15, 16, 17
		40, 40,
		2.0,
		`/img/wasm-popups/${scalePath}/wasm-popup-closure.png?version=${version}`,
	);

	const infoMapping = new module.ArrowMapping(
		52, 64,
		10, 32,
		58, -7,
		58, 32,
	);
	const weatherMapping = new module.ArrowMapping(
		42, 51,
		2, 23,
		48, -10,
		48, 21,
	);
	const closureMapping = new module.ArrowMapping(
		74, 90,
		4, 48,
		82, -15,
		82, 40,
	);

	return {
		infoWhite: {
			frame: infoWhiteFrame,
			arrow: infoMapping,
		},
		infoBlue: {
			frame: infoBlueFrame,
			arrow: infoMapping,
		},
		weather: {
			frame: weatherFrame,
			arrow: weatherMapping,
		},
		closure: {
			frame: closureFrame,
			arrow: closureMapping,
		},
	};
}

// smeruje pro 0 smerem dolu
function getPopupArrowAngles(value: Array<number> | undefined) {
	const angles = Array.isArray(value)
		? value
		: [];

	if (angles.length === 0) {
		angles.push(DEF_POPUP_ARROW_ANGLES.BOTTOM_LEFT);
	}

	const correctAngles = angles.map(angle => (angle + 180) % 360);

	return correctAngles;
}

function autoTextures(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, scale: number) {
	for (const textureName of AUTO_TEXTURES) {
		const textureData = getWasmTexture(wasm.textures, textureName, scale);
		const wasmTextureName = MAIN_TEXTURES[textureName];

		mapContent.registerPointElementVisual(wasm.getTextureData(wasmTextureName).id, [
			new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, textureData.width, textureData.height, textureData.anchorX, textureData.anchorY, wasmTextureName),
		]);
	}
}

function geoLocation(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, scale: number) {
	const geoLocationData = getWasmTexture(wasm.textures, "geo-location", scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(CUSTOM_TEXTURES["geo-location"]).id, [
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.radius) {
				const width = userData.radius;
				const url = new URL(getUrlProvider("circle-radius"));

				url.searchParams.append("color", TEXTURES_CONF.circleColorGeoLocation);
				url.searchParams.append("width", (TEXTURES_CONF.circleWidth * scale).toString());

				return new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Map, width, width, width / 2, width / 2, url.toString());
			}

			return null;
		},
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, geoLocationData.width, geoLocationData.height, geoLocationData.anchorX, geoLocationData.anchorY, MAIN_TEXTURES["geo-location"]),
	]);
}

function getBigTextures(module: typeof Module, bigActiveData: ReturnType<typeof getWasmTexture>, collisionGroups: ICollisionGroups, scale: number) {
	return (elementId: TWasmId, userData: IWasmCustomData) => {
		// musime dat do pole, pak vratit, jinak to ve wasm nefakci
		const parts: Array<Module.PointElementPart> = [];
		let reviewHeight = 0;

		if (userData?.review) {
			const reviewTexture = getReview(userData.review.value || WASM_TEXTURES.maxReview, userData.review.active, scale);
			const reviewWidth = reviewTexture.canvas.width / scale;

			reviewHeight = reviewTexture.canvas.height / scale;

			if (userData.review.value) {
				const reviewUrl = new URL(getUrlProvider("marker-review"));

				reviewUrl.searchParams.append("value", userData.review.value.toString());
				reviewUrl.searchParams.append("active", userData.review.active ? "1" : "0");
				reviewUrl.searchParams.append("scale", scale.toString());

				parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, reviewWidth, reviewHeight, -reviewWidth / 4, bigActiveData.height + 4, reviewUrl.toString()));
			}
		}

		if (userData?.caption) {
			const textTexture = getText(userData.caption, scale);
			const width = textTexture.canvas.width / scale;
			const height = textTexture.canvas.height / scale;
			const url = new URL(getUrlProvider("marker-text"));
			const leftRightPadding = 2;
			let leftRightY = (bigActiveData.height * 0.6) + (height / 2);

			if (textTexture.linesCount > 1 && reviewHeight > 0) {
				leftRightY -= 6;
			}

			url.searchParams.append("text", userData.caption);
			url.searchParams.append("scale", scale.toString());

			// text on right side of drop
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, -bigActiveData.anchorX - leftRightPadding, leftRightY, url.toString(), 0, collisionGroups.searchText.name, 0));
			// the same text on top of arrow,
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, (width / 2) - (bigActiveData.anchorX / 2), bigActiveData.height + (height / 2) + (reviewHeight / 2) + 4, url.toString(), 0, collisionGroups.searchText.name, 1));
			// the same text on bottom arrow,
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, (width / 2) - (bigActiveData.anchorX / 2), 0, url.toString(), 0, collisionGroups.searchText.name, 2));
			// the same text on left arrow,
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, bigActiveData.anchorX + width + leftRightPadding, leftRightY, url.toString(), 0, collisionGroups.searchText.name, 3));
		}

		if (parts.length === 0) {
			return null;
		}

		return parts;
	};
}

function big(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, textureName: TWasmTextureNames, collisionGroups: ICollisionGroups, scale: number) {
	const texture = MAIN_TEXTURES[textureName];
	const bigActiveData = getWasmTexture(wasm.textures, textureName, scale);
	const innerSize = 40;

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		collisionGroups.searchDrop.group,
		collisionGroups.searchText.group,
	], [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, bigActiveData.width, bigActiveData.height, bigActiveData.anchorX, bigActiveData.anchorY, texture, 0, collisionGroups.searchDrop.name, 0),
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, innerSize, innerSize, 20, 62, (elementId, userData) => {
			if (userData?.url) {
				const url = new URL(getUrlProvider("circle-image"));

				url.searchParams.append("img", userData.url);
				url.searchParams.append("size", innerSize.toString());
				url.searchParams.append("scale", scale.toString());
				// rozmery pro ikonu
				url.searchParams.append("iconsize", "30");

				return url.toString();
			}

			return "";
		}, 0, collisionGroups.searchDrop.name, 1),
		// hodnoceni
		getBigTextures(module, bigActiveData, collisionGroups, scale),
	]);
}

function bigUniversal(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, textureName: TWasmTextureNames, collisionGroups: ICollisionGroups, scale: number) {
	const texture = MAIN_TEXTURES[textureName];
	const bigActiveData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		collisionGroups.searchDrop.group,
		collisionGroups.searchText.group,
	], [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, bigActiveData.width, bigActiveData.height, bigActiveData.anchorX, bigActiveData.anchorY, texture, 0, collisionGroups.searchDrop.name, 0),
		// hodnoceni
		getBigTextures(module, bigActiveData, collisionGroups, scale),
	]);
}

interface IUserMarksConfig {
	active: boolean;
	opacity: number;
}

function userMarks(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, scale: number, configArg?: Partial<IUserMarksConfig>) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const config: IUserMarksConfig = {
		active: true,
		opacity: 1,
		...configArg,
	};
	const textureName: TWasmTextureNames = config.active ? "small-active" : "small-inactive";
	const smallData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, smallData.width, smallData.height, smallData.anchorX, smallData.anchorY, (elementId, userData) => {
			if (userData) {
				const url = new URL(getUrlProvider("user-marks"));

				url.searchParams.append("active", config.active ? "1" : "0");
				url.searchParams.append("opacity", config.opacity.toString());
				url.searchParams.append("order", userData.order.toString());
				url.searchParams.append("scale", scale.toString());

				return url.toString();
			}

			return "";
		}),
	]);
}

function small(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, isActive: boolean, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = isActive ? "small-active" : "small-inactive";
	const smallActiveData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, smallActiveData.width, smallActiveData.height, smallActiveData.anchorX, smallActiveData.anchorY, MAIN_TEXTURES[textureName]),
		(elementId: TWasmId, userData: IWasmCustomData) => {
			const size = 24;
			let textureUrl = "";

			if (userData?.textureName) {
				const url = new URL(getTextureName(userData.textureName));

				textureUrl = url.toString();
			} else if (userData?.url) {
				const url = new URL(getUrlProvider("circle-image"));

				url.searchParams.append("img", userData.url);
				url.searchParams.append("size", size.toString());
				url.searchParams.append("scale", scale.toString());
				// rozmery pro ikonu
				url.searchParams.append("iconsize", "20");

				textureUrl = url.toString();
			}

			if (textureUrl) {
				return new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, size, size, 12, 40, textureUrl);
			}

			return null;
		},
	]);
}

function smallGray(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = "small-gray";
	const smallGrayData = getWasmTexture(wasm.textures, textureName, scale);
	const innerSize = 24;

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, smallGrayData.width, smallGrayData.height, smallGrayData.anchorX, smallGrayData.anchorY, MAIN_TEXTURES[textureName]),
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, innerSize, innerSize, innerSize / 2, smallGrayData.anchorY - 6, (elementId, userData) => {
			if (userData?.url) {
				const url = new URL(getUrlProvider("circle-image"));

				url.searchParams.append("img", userData.url);
				url.searchParams.append("size", innerSize.toString());
				url.searchParams.append("scale", scale.toString());
				// rozmery pro ikonu
				url.searchParams.append("iconsize", "22");

				return url.toString();
			}

			return "";
		}),
	]);
}

function getSmallTextures(module: typeof Module, smallActiveData: ReturnType<typeof getWasmTexture>, collisionGroups: ICollisionGroups, scale: number) {
	return (elementId: TWasmId, userData: IWasmCustomData) => {
		// musime dat do pole, pak vratit, jinak to ve wasm nefakci
		const parts: Array<Module.PointElementPart> = [];
		let reviewHeight = 0;

		if (userData?.review) {
			const reviewTexture = getReview(userData.review.value || WASM_TEXTURES.maxReview, userData.review.active, scale);
			const reviewWidth = reviewTexture.canvas.width / scale;

			reviewHeight = reviewTexture.canvas.height / scale;

			if (userData.review.value) {
				const reviewUrl = new URL(getUrlProvider("marker-review"));

				reviewUrl.searchParams.append("value", userData.review.value.toString());
				reviewUrl.searchParams.append("active", userData.review.active ? "1" : "0");
				reviewUrl.searchParams.append("scale", scale.toString());

				parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, reviewWidth, reviewHeight, -reviewWidth / 4, smallActiveData.height + (reviewHeight * 0.3), reviewUrl.toString()));
			}
		}

		if (userData?.caption) {
			const textTexture = getText(userData.caption, scale);
			const width = textTexture.canvas.width / scale;
			const height = textTexture.canvas.height / scale;
			const url = new URL(getUrlProvider("marker-text"));
			const leftRightPadding = 2;
			let leftRightY = (smallActiveData.height * 0.6) + (height / 2);

			if (reviewHeight > 0) {
				if (textTexture.linesCount > 1) {
					leftRightY -= 12;
				} else {
					leftRightY -= 6;
				}
			}

			url.searchParams.append("text", userData.caption);
			url.searchParams.append("scale", scale.toString());

			// text on right side of drop
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, -smallActiveData.anchorX - leftRightPadding, leftRightY, url.toString(), 0, collisionGroups.searchText.name, 0));
			// the same text on top of arrow,
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, (width / 2) - (smallActiveData.anchorX / 2), smallActiveData.height + (height / 2) + reviewHeight, url.toString(), 0, collisionGroups.searchText.name, 1));
			// the same text on bottom arrow,
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, (width / 2) - (smallActiveData.anchorX / 2), 0, url.toString(), 0, collisionGroups.searchText.name, 2));
			// the same text on left arrow,
			parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, smallActiveData.anchorX + width + leftRightPadding, leftRightY, url.toString(), 0, collisionGroups.searchText.name, 3));
		}

		if (parts.length === 0) {
			return null;
		}

		return parts;
	};
}

function smallUniversal(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, textureName: TWasmTextureNames, collisionGroups: ICollisionGroups, scale: number) {
	const texture = MAIN_TEXTURES[textureName];
	const smallActiveData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		collisionGroups.searchDrop.group,
		collisionGroups.searchText.group,
	], [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, smallActiveData.width, smallActiveData.height, smallActiveData.anchorX, smallActiveData.anchorY, MAIN_TEXTURES[textureName], 0, collisionGroups.searchDrop.name, 0),
		getSmallTextures(module, smallActiveData, collisionGroups, scale),
	]);
}

interface IRouteConfig {
	active: boolean;
	start: boolean;
	opacity: number;
}

function route(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, scale: number, configArg?: Partial<IRouteConfig>) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const config: IRouteConfig = {
		active: true,
		start: true,
		opacity: 1,
		...configArg,
	};
	const bigActiveData = getWasmTexture(wasm.textures, config.active ? "big-active" : "big-inactive", scale);
	const url = new URL(getUrlProvider("route"));

	url.searchParams.append("active", config.active ? "1" : "0");
	url.searchParams.append("start", config.start ? "1" : "0");
	url.searchParams.append("opacity", config.opacity.toString());
	url.searchParams.append("scale", scale.toString());
	// big vnitrni sirka
	url.searchParams.append("textWidth", "40");

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, bigActiveData.width, bigActiveData.height, bigActiveData.anchorX, bigActiveData.anchorY, url.toString()),
	]);
}

function searchBadgeText(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: string, textureName: TWasmTextureNames, collisionGroups: ICollisionGroups, scale: number) {
	const smallActiveData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(bindTexture).id, [
		collisionGroups.searchDrop.group,
		collisionGroups.searchText.group,
	], [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, smallActiveData.width, smallActiveData.height, smallActiveData.anchorX, smallActiveData.anchorY, MAIN_TEXTURES[textureName], 0, collisionGroups.searchDrop.name, 0),
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, 20, 20, 10, 38, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}, 0, collisionGroups.searchDrop.name, 1),
		getSmallTextures(module, smallActiveData, collisionGroups, scale),
	]);
}

function photo(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, isActive: boolean, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = isActive
		? "photo-active"
		: "photo-inactive";
	const photoData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, photoData.width, photoData.height, photoData.anchorX, photoData.anchorY, MAIN_TEXTURES[textureName]),
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, 25, 25, 14, 14, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}),
	]);
}

function webcam(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, isActive: boolean, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = isActive
		? "webcam-active"
		: "webcam-inactive";
	const webcamData = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, webcamData.width, webcamData.height, webcamData.anchorX, webcamData.anchorY, MAIN_TEXTURES[textureName]),
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, 56, 41, 30, 22, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}),
	]);
}

function positionShareDevice(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, isActive: boolean, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const positionTexture = getWasmTexture(wasm.textures, isActive ? "position-share-device-active" : "position-share-device-inactive", scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, positionTexture.width, positionTexture.height, positionTexture.anchorX, positionTexture.anchorY, (elementId, userData) => {
			if (userData?.order) {
				const url = new URL(getUrlProvider("position-share-device"));

				url.searchParams.append("order", userData.order.toString());
				url.searchParams.append("active", isActive ? "1" : "0");
				url.searchParams.append("scale", scale.toString());

				return url.toString();
			}

			return "";
		}),
	]);
}

function positionShareImage(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, variant: TPositionShareImageVariant, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = variant === "active"
		? "position-share-image-active"
		: "position-share-image-inactive";
	const positionTexture = getWasmTexture(wasm.textures, textureName, scale);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, positionTexture.width, positionTexture.height, positionTexture.anchorX, positionTexture.anchorY, (elementId, userData) => {
			if (userData?.url) {
				const url = new URL(getUrlProvider("position-share-image"));

				url.searchParams.append("img", userData.url);
				url.searchParams.append("variant", variant);
				url.searchParams.append("scale", scale.toString());

				return url.toString();
			}

			return "";
		}),
	]);
}

function positionShareImageRadius(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.radius) {
				const width = userData.radius;
				const url = new URL(getUrlProvider("circle-radius"));

				url.searchParams.append("color", TEXTURES_CONF.circleColorPositionShare);
				url.searchParams.append("width", (TEXTURES_CONF.circleWidth * scale).toString());

				return new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Map, width, width, width / 2, width / 2, url.toString());
			}

			return null;
		},
	]);
}

function pathCaption(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId, userData) => {
			if (userData?.caption) {
				const captionTexture = getPathCaption(userData.caption, scale);
				const parts: Array<Module.PointElementPart> = [];
				const width = captionTexture.canvas.width / scale;
				const height = captionTexture.canvas.height / scale;
				const url = new URL(getUrlProvider("path-caption"));

				url.searchParams.append("text", userData.caption);
				url.searchParams.append("scale", scale.toString());
				parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString()));

				return parts;
			}

			return null;
		},
	]);
}

function routePointCircle(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId, userData) => {
			if (userData?.routePoint) {
				const captionTexture = getRoutePointCircle(userData.routePoint.radius, scale, userData.routePoint.color);
				const parts: Array<Module.PointElementPart> = [];
				const width = captionTexture.canvas.width / scale;
				const height = captionTexture.canvas.height / scale;
				const url = new URL(getUrlProvider("route-point-circle"));

				url.searchParams.append("radius", userData.routePoint.radius.toString());
				url.searchParams.append("color", userData.routePoint.color);
				url.searchParams.append("scale", scale.toString());
				parts.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString()));

				return parts;
			}

			return null;
		},
	]);
}

function routePopupInfo(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, variant: TRoutePopupInfoColorVariant, collisionGroups: ICollisionGroups, popupMappings: IPopupMapping, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPopupVisual(wasm.getTextureData(texture).id,
		variant === "active" ? popupMappings.infoBlue.frame : popupMappings.infoWhite.frame,
		variant === "active" ? popupMappings.infoBlue.arrow : popupMappings.infoWhite.arrow,
		collisionGroups.routePopupInfo.group,
		// nesmi byt async!
		(elementId, userData) => {
			if (userData?.routePopupInfo) {
				const linesData = createRoutePopupContentLines(userData.routePopupInfo, variant, scale);
				const url = new URL(getUrlProvider("route-popup-info"));

				url.searchParams.append("json", JSON.stringify(userData.routePopupInfo));
				url.searchParams.append("variant", variant);
				url.searchParams.append("scale", scale.toString());

				const canvas = linesData.canvas;
				const width = canvas.width / scale;
				const height = canvas.height / scale;
				const popupVariants = [];
				const angles = getPopupArrowAngles(userData.popupArrowAngles);

				for (let ind = 0, max = angles.length; ind < max; ind++) {
					popupVariants.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString(), angles[ind], collisionGroups.routePopupInfo.name, ind));
				}

				return popupVariants;
			}

			return null;
		});
}

function routePopupWeather(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, variant: "temp" | "precp" | "wind", collisionGroups: ICollisionGroups, popupMappings: IPopupMapping, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPopupVisual(wasm.getTextureData(texture).id,
		popupMappings.weather.frame,
		popupMappings.weather.arrow,
		collisionGroups.routePopupWeather.group,
		(elementId, userData) => {
			if (userData?.routePopupWeather) {
				let icon = "";
				let hasIcon = false;
				let value = "";
				let textBold = true;
				const time = userData.routePopupWeather.time || "";
				const url = new URL(getUrlProvider("route-popup-weather"));

				if (variant === "temp") {
					icon = userData.routePopupWeather.temp.icon;
					value = userData.routePopupWeather.temp.value;
					hasIcon = true;
				} else if (variant === "precp") {
					icon = userData.routePopupWeather.prec.icon || "";
					value = userData.routePopupWeather.prec.value;
					url.searchParams.append("forcefill", "#006ed2");

					if (icon) {
						hasIcon = true;
					} else {
						textBold = false;
					}
				} else if (variant === "wind") {
					value = userData.routePopupWeather.wind.value;
					hasIcon = true;
					url.searchParams.append("windangle", userData.routePopupWeather.wind.angle.toString());
				}

				url.searchParams.append("icon", icon);
				url.searchParams.append("value", value);
				url.searchParams.append("time", time);
				url.searchParams.append("bold", textBold ? "1" : "0");
				url.searchParams.append("scale", scale.toString());

				const canvas = getWeatherIconTextureCanvas(hasIcon, value, time, scale, textBold).canvas;
				const width = canvas.width / scale;
				const height = canvas.height / scale;
				const popupVariants = [];
				const angles = getPopupArrowAngles(userData.popupArrowAngles);

				for (let ind = 0, max = angles.length; ind < max; ind++) {
					popupVariants.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString(), angles[ind], collisionGroups.routePopupWeather.name, ind));
				}

				return popupVariants;
			}

			return null;
		});
}

function routePopupPubtSummary(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, collisionGroups: ICollisionGroups, popupMappings: IPopupMapping, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPopupVisual(wasm.getTextureData(texture).id,
		popupMappings.infoWhite.frame,
		popupMappings.infoWhite.arrow,
		collisionGroups.routePopupPubt.group,
		// nesmi byt async!
		(elementId, userData) => {
			if (userData?.routePopupPubtSummary) {
				const linesData = createRoutePopupPubtSummary(wasm.textures, userData.routePopupPubtSummary, scale);
				const url = new URL(getUrlProvider("route-popup-pubt-summary"));

				url.searchParams.append("text", userData.routePopupPubtSummary.text);
				url.searchParams.append("warning", userData.routePopupPubtSummary.warning ? "1" : "0");
				url.searchParams.append("icons", JSON.stringify(userData.routePopupPubtSummary.icons));
				url.searchParams.append("scale", scale.toString());

				const canvas = linesData.canvas;
				const width = canvas.width / scale;
				const height = canvas.height / scale;
				const popupVariants = [];
				const angles = getPopupArrowAngles(userData.popupArrowAngles);

				for (let ind = 0, max = angles.length; ind < max; ind++) {
					popupVariants.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString(), angles[ind], collisionGroups.routePopupPubt.name, ind));
				}

				return popupVariants;
			}

			return null;
		});
}

function routePopupPubtSection(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, collisionGroups: ICollisionGroups, popupMappings: IPopupMapping, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPopupVisual(wasm.getTextureData(texture).id,
		popupMappings.infoWhite.frame,
		popupMappings.infoWhite.arrow,
		collisionGroups.routePopupPubt.group,
		// nesmi byt async!
		(elementId, userData) => {
			if (userData?.routePopupPubtSection) {
				const linesData = createRoutePopupPubtSection(wasm.textures, userData.routePopupPubtSection, scale);
				const url = new URL(getUrlProvider("route-popup-pubt-section"));

				url.searchParams.append("title", userData.routePopupPubtSection.title);
				url.searchParams.append("subtitle", userData.routePopupPubtSection.subTitle);
				url.searchParams.append("desc", userData.routePopupPubtSection.desc);
				url.searchParams.append("titlecolor", userData.routePopupPubtSection.titleColor);
				url.searchParams.append("desccolor", userData.routePopupPubtSection.descColor);
				url.searchParams.append("scale", scale.toString());

				const canvas = linesData.canvas;
				const width = canvas.width / scale;
				const height = canvas.height / scale;
				const popupVariants = [];
				const angles = getPopupArrowAngles(userData.popupArrowAngles);

				for (let ind = 0, max = angles.length; ind < max; ind++) {
					popupVariants.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString(), angles[ind], collisionGroups.routePopupPubt.name, ind));
				}

				return popupVariants;
			}

			return null;
		});
}

function routePopupPubtClosure(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, collisionGroups: ICollisionGroups, popupMappings: IPopupMapping, scale: number) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPopupVisual(wasm.getTextureData(texture).id,
		popupMappings.closure.frame,
		popupMappings.closure.arrow,
		collisionGroups.routePopupClosure.group,
		// nesmi byt async!
		(elementId, userData) => {
			if (userData?.routePopupClosure) {
				const linesData = createRoutePopupClosure(wasm.textures, userData.routePopupClosure, scale);
				const url = new URL(getUrlProvider("route-popup-closure"));

				url.searchParams.append("icon", userData.routePopupClosure.icon);
				url.searchParams.append("text", userData.routePopupClosure.text);
				url.searchParams.append("scale", scale.toString());

				const canvas = linesData.canvas;
				const width = canvas.width / scale;
				const height = canvas.height / scale;
				const popupVariants = [];
				const angles = getPopupArrowAngles(userData.popupArrowAngles);

				for (let ind = 0, max = angles.length; ind < max; ind++) {
					popupVariants.push(new module.PointElementPart(TEXTURES_CONF.priorityLayer, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString(), angles[ind], collisionGroups.routePopupClosure.name, ind));
				}

				return popupVariants;
			}

			return null;
		});
}

export function registerTextures(wasm: Wasm, scale: number) {
	const mapContent = wasm.mapContent;
	const module = wasm.module;
	const collisionGroups = createCollisionGroups(module);
	const popupMappings = createPopupMappings(module, scale);

	// vycet auto ikon
	autoTextures(wasm, mapContent, module, scale);
	// geo-location
	geoLocation(wasm, mapContent, module, scale);
	// big-active/inactive
	big(wasm, mapContent, module, "big-active", collisionGroups, scale);
	big(wasm, mapContent, module, "big-inactive", collisionGroups, scale);
	// big-universal-active/inactive
	bigUniversal(wasm, mapContent, module, "big-universal-active", collisionGroups, scale);
	bigUniversal(wasm, mapContent, module, "big-universal-inactive", collisionGroups, scale);
	// user-marks-active/inactive
	userMarks(wasm, mapContent, module, "user-marks-active", scale);
	userMarks(wasm, mapContent, module, "user-marks-inactive", scale, { active: false });
	userMarks(wasm, mapContent, module, "user-marks-opacity-active", scale, { opacity: TEXTURES_CONF.opacity });
	// small-active/inactive
	small(wasm, mapContent, module, "small-active", true, scale);
	small(wasm, mapContent, module, "small-inactive", false, scale);
	smallGray(wasm, mapContent, module, "small-gray", scale);
	// small-universal-active/inactive
	smallUniversal(wasm, mapContent, module, "small-universal-active", collisionGroups, scale);
	smallUniversal(wasm, mapContent, module, "small-universal-inactive", collisionGroups, scale);
	// route-start-active/inactive
	route(wasm, mapContent, module, "route-start-active", scale);
	route(wasm, mapContent, module, "route-start-inactive", scale, { active: false });
	// route-end-active/inactive
	route(wasm, mapContent, module, "route-end-active", scale, { start: false });
	route(wasm, mapContent, module, "route-end-inactive", scale, { active: false, start: false });
	// route-start-opacity-active/inactive
	route(wasm, mapContent, module, "route-start-opacity-active", scale, { opacity: TEXTURES_CONF.opacity });
	route(wasm, mapContent, module, "route-end-opacity-active", scale, { start: false, opacity: TEXTURES_CONF.opacity });
	// search-badge-text-active/inactive
	searchBadgeText(wasm, mapContent, module, CUSTOM_TEXTURES["search-badge-text-active"], "small-active", collisionGroups, scale);
	searchBadgeText(wasm, mapContent, module, CUSTOM_TEXTURES["search-badge-text-inactive"], "small-inactive", collisionGroups, scale);
	// photo-active/inactive
	photo(wasm, mapContent, module, "photo-active", true, scale);
	photo(wasm, mapContent, module, "photo-inactive", false, scale);
	// webcam-active/inactive
	webcam(wasm, mapContent, module, "webcam-inactive", false, scale);
	webcam(wasm, mapContent, module, "webcam-active", true, scale);
	// position-share-device-active/inactive
	positionShareDevice(wasm, mapContent, module, "position-share-device-active", true, scale);
	positionShareDevice(wasm, mapContent, module, "position-share-device-inactive", false, scale);
	// position-share-image-active|inactive|gray
	positionShareImage(wasm, mapContent, module, "position-share-image-active", "active", scale);
	positionShareImage(wasm, mapContent, module, "position-share-image-inactive", "inactive", scale);
	positionShareImage(wasm, mapContent, module, "position-share-image-gray-active", "gray-active", scale);
	positionShareImage(wasm, mapContent, module, "position-share-image-gray-inactive", "gray-inactive", scale);
	// position-share-image-radius
	positionShareImageRadius(wasm, mapContent, module, "position-share-image-radius", scale);
	// path-caption
	pathCaption(wasm, mapContent, module, "path-caption", scale);
	// route-point-circle
	routePointCircle(wasm, mapContent, module, "route-point-circle", scale);
	// route-popup-info-white/blue
	routePopupInfo(wasm, mapContent, module, "route-popup-info-white", "inactive", collisionGroups, popupMappings, scale);
	routePopupInfo(wasm, mapContent, module, "route-popup-info-blue", "active", collisionGroups, popupMappings, scale);
	// route-popup-weather-temp/precp/rain
	routePopupWeather(wasm, mapContent, module, "route-popup-weather-temp", "temp", collisionGroups, popupMappings, scale);
	routePopupWeather(wasm, mapContent, module, "route-popup-weather-precp", "precp", collisionGroups, popupMappings, scale);
	routePopupWeather(wasm, mapContent, module, "route-popup-weather-wind", "wind", collisionGroups, popupMappings, scale);
	// route-popup-pubt-summary
	routePopupPubtSummary(wasm, mapContent, module, "route-popup-pubt-summary", collisionGroups, popupMappings, scale);
	// route-popup-pubt-section
	routePopupPubtSection(wasm, mapContent, module, "route-popup-pubt-section", collisionGroups, popupMappings, scale);
	// route-popup-closure
	routePopupPubtClosure(wasm, mapContent, module, "route-popup-closure", collisionGroups, popupMappings, scale);
}
